import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Params, RouterLink } from '@angular/router';
import { CalioIconComponent } from '@calio-lib/calio-button/calio-icon/calio-icon.component';
import { CalioMatIconComponent } from '@calio-lib/calio-button/calio-mat-icon/calio-mat-icon.component';
import { CalioLockIconComponent } from '@calio-lib/calio-lock-icon/calio-lock-icon.component';
import { CalioFeaturePipe } from '@calio-lib/calio-pipes/calio-feature.pipe';
import { CalioPermissionCheckerPipe } from '@calio-lib/calio-pipes/calio-permission-checker.pipe';

const components = [CalioMatIconComponent, CalioIconComponent, CalioLockIconComponent];
const pipes = [CalioPermissionCheckerPipe, CalioFeaturePipe];

/**
 * Calio Flat Button Component
 *
 * A versatile button component supporting multiple styles and icon types.
 * All icons are now rendered using Lucide via calio-mat-icon.
 */
@Component({
  selector: 'app-calio-flat-button',
  templateUrl: './calio-flat-button.component.html',
  styleUrls: ['./calio-flat-button.component.scss'],
  imports: [NgStyle, NgTemplateOutlet, RouterLink, ...pipes, ...components],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.cfb-full-width]': 'fullWidth()',
  },
})
export class CalioFlatButtonComponent {
  customClass = input<string>('');
  iconCustomClass = input<string>('');
  disabled = input(false, { transform: booleanAttribute });
  buttonText = input<string>('');
  type = input<string>('');
  buttonType = input('button');
  enableRoutingHyperLink = input(false, { transform: booleanAttribute });
  linkOnly = input(false, { transform: booleanAttribute });
  url = input<string>('');
  queryParams = input<Params>();
  /**
   * Router fragment for the routing-link render mode (added on dev).
   *
   * NO default on purpose. Angular's serializer appends '#' for ANY string fragment,
   * including the empty one (`f ?? null` lets '' through, then
   * `typeof tree.fragment === 'string'` is true), so a default of '' would put a trailing
   * '#' on the href of every routing-link button that passes no fragment - 26 of the 27
   * templates that use this mode. Leaving it undefined matches both dev's original
   * `@Input() fragment: string` and the `queryParams` line above.
   */
  fragment = input<string>();
  /**
   * Icon rendered at a fixed 16px next to the label — the default choice for button icons,
   * and the only icon input the link render modes support. Use `iconType` instead only when
   * the icon needs its own sizing/fill (`svgHeight`/`svgWidth`/`svgFill`), which defaults to 24px.
   */
  icon = input<string>('');
  height = input<string>('');
  minWidth = input<string>('');
  permissionTypes = input<string | string[]>();
  target = input('_self');
  buttonId = input('');
  enableExternalHyperLink = input(false, { transform: booleanAttribute });
  isloading = input(false, { transform: booleanAttribute });
  requiredFeatureName = input('');
  iconType = input<string>('');
  customSvgClass = input<string>('');
  svgHeight = input<string>('');
  svgWidth = input<string>('');
  svgFill = input<string>('');
  /** Size variant — emits `tw-btn-sm` / `tw-btn-lg`. Empty string = default size. */
  size = input<'' | 'sm' | 'lg'>('');
  /**
   * Which side of the label the icon sits on. Defaults to `before`, which is what every
   * call site rendered when the order was hardcoded, so this input changes nothing until
   * it is set.
   *
   * `after` exists for the "continue / back to live"-style actions whose arrow trails the
   * text; without it those stayed hand-rolled, which is the same
   * missing-capability-breeds-a-copy loop that `shape="circle"` and `quiet` each closed.
   * Ignored when `iconOnly` is set — there is no label to sit beside.
   */
  iconPosition = input<'before' | 'after'>('before');
  /**
   * Suppresses the button label, leaving a square icon-only affordance (`.tw-btn-icon`)
   * with `buttonText` exposed as the `aria-label`. On the `linkOnly` render mode the label
   * is suppressed and the aria-label applied, but no `.tw-btn-icon` chrome is added
   * (linkOnly deliberately has no `tw-btn` base styling to square up).
   */
  iconOnly = input(false, { transform: booleanAttribute });
  /**
   * Shape variant — `circle` emits `.tw-btn-circle`, a 36x36 fully-round control that owns the
   * circular badge geometry (and, on `ghost`, the badge ring) which used to be hand-rolled as a
   * wrapper `<span class="rounded-icon">` around the button. Pair it with `iconOnly`, and keep
   * passing `buttonText` — it becomes the `aria-label`.
   *
   * Not an on/off flag on purpose: the shape axis can later gain `pill`/`square` without a second
   * boolean input. The `linkOnly` render mode ignores it, exactly like `iconOnly` does — that mode
   * deliberately renders no `.tw-btn` chrome to shape.
   */
  shape = input<'default' | 'circle'>('default');
  /**
   * Quiet treatment — **muted at rest, the variant's own colour on hover (and on keyboard
   * focus)**, with no box of its own. This is the treatment for **table-row action icons**
   * and **inline builder actions** ("+ Add condition"), the two places that kept getting
   * hand-rolled because `ghost` colours the icon at rest and `link` forces a 40px box.
   *
   * It is a MODIFIER rather than an 11th variant on purpose: the colour it reveals is the
   * variant's, so ONE input covers the neutral pencil (`ghost`), the destructive trash
   * (`ghost-danger`) and the primary text action (`link`) — which as variants would have
   * needed `quiet` + `quiet-danger` + `quiet-primary`. A neutral and a destructive icon in
   * the SAME table row is the case that decides it.
   *
   * Pair it with a chrome-less variant (`ghost`, `ghost-danger`, `link`). It imposes no
   * padding, so compose it with `iconOnly` (36x36 hit area) or `size="sm"` when the control
   * needs a box, and use it bare in a zero-padding inline row. Ignored by `linkOnly`, like
   * `iconOnly` and `shape`.
   */
  quiet = input(false, { transform: booleanAttribute });
  /** Makes the host + its rendered element(s) stretch to 100% width of the parent. */
  fullWidth = input(false, { transform: booleanAttribute });
  /**
   * Stops the click event from bubbling up to a parent that also listens for clicks
   * (e.g. a clickable card row, or a draggable list item) — so this button's click
   * doesn't also trigger the parent's own click/drag handler.
   */
  stopPropagation = input(false, { transform: booleanAttribute });

  clickEvent = output<void>();

  /**
   * Icon name passed through to calio-mat-icon.
   */
  protected iconName = computed(() => this.icon());

  protected onClick(event?: MouseEvent): void {
    if (this.stopPropagation()) {
      event?.stopPropagation();
    }
    if (!this.isloading() && !this.disabled()) {
      this.clickEvent.emit();
    }
  }

  /**
   * Link render modes navigate via routerLink/href rather than a click handler, so they
   * deliberately do NOT emit `clickEvent` — this hook only honours `stopPropagation` so a
   * link-style button nested in a clickable parent can isolate its own click.
   */
  protected onLinkClick(event?: MouseEvent): void {
    if (this.stopPropagation()) {
      event?.stopPropagation();
    }
  }
}
